import * as React from 'react'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useTranslation } from "react-i18next"
import { Tooltip } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import TablePagination from "@mui/material/TablePagination";
import { t } from 'i18next'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { visuallyHidden } from '@mui/utils';
import Swal from 'sweetalert2'
import axios from '../../lib/axios'
import { useStateContext } from '../../context/ContextProvider'
import { useAuth } from '../../hooks/auth'
import { canViewPrice, isValid, formatCurrency, formatQuantity } from '../../helpers/helper'

export const QUOTE_STATUS_STYLES = {
    draft: { background: '#E8E8E8', color: '#515151' },
    send: { background: '#FFE8CC', color: '#E65100' },
    accepted: { background: '#BEFFD6', color: '#017C40' },
    rejected: { background: '#FFD1D1', color: '#B81100' },
    converted: { background: '#D5EDFB', color: '#008A93' },
}

const QuoteStatus = ({ status }) => {
    const key = String(status).toLowerCase()
    const isKnown = key in QUOTE_STATUS_STYLES
    const style = QUOTE_STATUS_STYLES[key] ?? { background: '#E8E8E8', color: '#515151' }

    return (
        <div
            className='w-full rounded-md p-1 text-center font-[500] capitalize'
            style={{ backgroundColor: style.background, color: style.color }}
        >
            {isKnown ? t(`quote_status_${key}`) : status}
        </div>
    )
}

function Row(props) {
    const { row, onQuoteDeleted } = props;
    const [open, setOpen] = React.useState(false)
    const { t } = useTranslation()
    const { user } = useAuth({ middleware: 'guest' })
    const { features, currencySymbol } = useStateContext()
    const canSeePrices = canViewPrice(user, features)

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset !important' } }}>
                <TableCell sx={{ padding: '8px 16px', fontWeight: '500' }}>{row.quote_number}</TableCell>
                <TableCell sx={{ padding: '8px 16px', fontStyle: 'italic' }}>{row.customer_name}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.ship_address}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.quote_date ? dayjs(row.quote_date).format("YYYY-MM-DD") : ''}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.valid_until ? <ShowDate date={row.valid_until} /> : ''}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.requested_delivery_date ? <ShowDate date={row.requested_delivery_date} /> : ''}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.salesperson_name}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>
                    {row.quote_status ? <QuoteStatus status={row.quote_status} /> : ''}
                </TableCell>
                <TableCell sx={{ padding: '8px 5px' }}>
                    <QuoteActions id={row.id} quoteNumber={row.quote_number} status={row.quote_status} onDeleted={onQuoteDeleted} />
                </TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell style={{ padding: 0 }} colSpan={10}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box>
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table size="small" aria-label="quote-items">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('item__no')}</TableCell>
                                            <TableCell>{t('description')}</TableCell>
                                            <TableCell>{t('uom')}</TableCell>
                                            <TableCell>{t('vendor')}</TableCell>
                                            <TableCell>{t('quantity')}</TableCell>
                                            {canSeePrices ? (
                                                <>
                                                    <TableCell>{t('source_cost')}</TableCell>
                                                    <TableCell>{t('freight_cost')}</TableCell>
                                                    <TableCell>{t('fuel_surcharge')}</TableCell>
                                                    <TableCell>{t('other_costs')}</TableCell>
                                                    <TableCell>{t('calculated_cost')}</TableCell>
                                                    <TableCell>{t('unit_price')}</TableCell>
                                                    <TableCell>{t('total_price')}</TableCell>
                                                    <TableCell>{t('margin_dollar')}</TableCell>
                                                    <TableCell>{t('margin_percent')}</TableCell>
                                                </>
                                            ) : null}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {row.quote_items?.map((historyRow) => (
                                            <TableRow key={historyRow.id}>
                                                <TableCell sx={{ padding: '8px 16px' }}>{historyRow.item_no}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{historyRow.description}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{historyRow.unit_of_measure_code}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{historyRow.source_vendor_name}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.quantity) ? formatQuantity(historyRow.quantity) : ''}</TableCell>
                                                {canSeePrices ? (
                                                    <>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.source_cost) ? `${currencySymbol}${formatCurrency(historyRow.source_cost)}` : t('coming_soon')}</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.freight_cost) ? `${currencySymbol}${formatCurrency(historyRow.freight_cost)}` : t('coming_soon')}</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.fuel_surcharge) ? `${currencySymbol}${formatCurrency(historyRow.fuel_surcharge)}` : t('coming_soon')}</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.other_costs) ? `${currencySymbol}${formatCurrency(historyRow.other_costs)}` : t('coming_soon')}</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.calculated_cost) ? `${currencySymbol}${formatCurrency(historyRow.calculated_cost)}` : t('coming_soon')}</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.unit_price) ? `${currencySymbol}${formatCurrency(historyRow.unit_price)}` : t('coming_soon')}</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.total_price) ? `${currencySymbol}${formatCurrency(historyRow.total_price)}` : t('coming_soon')}</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.margin_dollar) ? `${currencySymbol}${formatCurrency(historyRow.margin_dollar)}` : t('coming_soon')}</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px' }}>{isValid(historyRow.margin_percent) ? `${formatCurrency(historyRow.margin_percent)}%` : t('coming_soon')}</TableCell>
                                                    </>
                                                ) : null}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

        </React.Fragment>
    );
}

const QuoteActions = ({ id, quoteNumber, status, onDeleted }) => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { config } = useStateContext()

    const deleteQuote = async () => {
        const isConfirm = await Swal.fire({
            title: t('title_delete') + t('del_quote') + (quoteNumber ?? id) + "?",
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,

            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        }).then((result) => result.isConfirmed)

        if (!isConfirm) {
            return
        }

        await axios.delete(`/api/quotes/${id}`, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })

            if (onDeleted) {
                onDeleted()
            }
        }).catch(({ response }) => {
            Swal.fire({
                text: response?.data?.message,
                icon: "error",
                customClass: 'error',
                showCloseButton: true,
                iconColor: '#FF0000'
            })
        })
    }

    return (
        <div className='flex gap-1'>
            <Tooltip disableInteractive title={t('view')} placement='bottom'>
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <button onClick={() => navigate(`${id}/view`, { state: { prevPathname: location.pathname } })}><span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"><i className="fa-solid fa-eye"></i></span></button>
                </div>
            </Tooltip>
            <Tooltip disableInteractive title={t('update')} placement='bottom'>
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <button onClick={() => navigate(`${id}`, { state: { prevPathname: location.pathname } })}><span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"><i className="fa-solid fa-pencil"></i></span></button>
                </div>
            </Tooltip>
            <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={deleteQuote}><i className="fa-solid fa-trash"></i></span>
                </div>
            </Tooltip>
        </div>
    )
}

const SortableTableHeaderRow = (props) => {
    const { order, orderBy, onRequestSort } = props
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property)
    }

    const columns = [
        { id: 'quote_number', label: t('quote_no') },
        { id: 'customer_name', label: t('customer') },
        { id: 'ship_to_location', label: t('ship_to_location') },
        { id: 'quote_date', label: t('quote_date') },
        { id: 'valid_until', label: t('valid_until') },
        { id: 'requested_delivery_date', label: t('requested_delivery_date') },
        { id: 'salesperson_name', label: t('salesperson_owner') },
        { id: 'quote_status', label: t('quote_status') },
    ]

    return (
        <TableHead>
            <TableRow>
                {columns.map((column) => (
                    <TableCell key={column.id} sortDirection={orderBy === column.id ? order : false}>
                        <TableSortLabel sx={{ color: '#88909C !important', fontWeight: '400' }}
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={createSortHandler(column.id)}>
                            {column.label}
                            {orderBy === column.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell sx={{ color: '#88909C !important', fontWeight: '400' }}>{t('actions')}</TableCell>
                <TableCell />
            </TableRow>
        </TableHead>
    )
}

export default function CollapsibleTable({ items, total, page = 1, rowsPerPage = 10, opc = undefined, orppc = undefined, osc = undefined, onQuoteDeleted = undefined }) {

    const { t } = useTranslation()

    const pg = Number(page - 1)
    const rpg = Number(rowsPerPage)

    const [orderBy, setOrderBy] = useState('created_at')
    const [order, setOrder] = useState('desc')

    /*
     * The backend doesn't support sorting quotes yet (no `sort` query param on GET /api/quotes),
     * so this sorts the currently-loaded page client-side. Once the backend adds it, wire `osc`
     * through to the API call the way other list pages do instead of sorting here.
     */
    const sortFieldGetters = {
        quote_number: (row) => row.quote_number ?? '',
        customer_name: (row) => row.customer_name ?? '',
        ship_to_location: (row) => row.ship_address ?? '',
        quote_date: (row) => row.quote_date ?? '',
        valid_until: (row) => row.valid_until ?? '',
        requested_delivery_date: (row) => row.requested_delivery_date ?? '',
        salesperson_name: (row) => row.salesperson_name ?? '',
        quote_status: (row) => row.quote_status ?? '',
    }

    const sortedItems = useMemo(() => {
        const getValue = sortFieldGetters[orderBy]
        if (!getValue) return items

        return [...items].sort((a, b) => {
            const valueA = getValue(a)
            const valueB = getValue(b)

            if (valueA < valueB) return order === 'asc' ? -1 : 1
            if (valueA > valueB) return order === 'asc' ? 1 : -1
            return 0
        })
    }, [items, orderBy, order])

    function handleChangePage(event, newpage) {
        if(opc) {
            opc(Number(newpage + 1));
        }
    }

    function handleChangeRowsPerPage(event) {
        if(orppc) {
            opc(1);
            orppc(parseInt(event.target.value, 10));
        }
    }

    const handleRequestSort = (event, property) => {
        if (order === 'desc' && orderBy === property) {
            setOrderBy('created_at')
            if(osc) {
                osc('created_at,desc')
            }
        } else {
            const isAsc = orderBy === property && order === 'asc'
            setOrder(isAsc ? 'desc' : 'asc')
            setOrderBy(property)
            if(osc) {
                osc(`${property},${isAsc ? 'desc' : 'asc'}`)
            }
        }
    }

    return (
        <div>
            <div style={{ width: '100%', mb: 2, border: 'none' }}>
                <TableContainer>
                    <Table aria-label="collapsible table" stickyHeader>
                        <colgroup>
                            <col width="9%" />
                            <col width="12%" />
                            <col width="12%" />
                            <col width="9%" />
                            <col width="9%" />
                            <col width="11%" />
                            <col width="11%" />
                            <col width="9%" />
                            <col width="8%" />
                            <col />
                        </colgroup>
                        <SortableTableHeaderRow
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                        />
                        <TableBody>
                            {sortedItems.length <= 0 ? <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center' }}>{t('no_data')}</TableCell></TableRow> : null}
                            {sortedItems?.map((row) => (
                                <Row key={row.id} row={row} onQuoteDeleted={onQuoteDeleted} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    className='table-pagination'
                    labelRowsPerPage={t('rowsPerPage')}
                    rowsPerPageOptions={[10, 15]}
                    component="div"
                    count={total}
                    rowsPerPage={rpg}
                    page={pg}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </div>
        </div>
    );
}

export const ShowDate = ({ date }) => {

    const date1 = new Date(dayjs(date).format("YYYY-MM-DD"))
    const date2 = new Date(dayjs().format("YYYY-MM-DD"))

    return (
        <div>
            {
                date1 < date2 ? <p style={{ color: '#F00', padding: '5px', lineHeight: '1', width: 'fit-content' }}>{dayjs(date).format("MMMM D, YYYY")}</p> :
                    date1 == date2 ? <p style={{ color: 'blue', padding: '5px', lineHeight: '1', width: 'fit-content' }}>{dayjs(date).format("MMMM D, YYYY")}</p> :
                        date1 > date2 ? <p style={{ color: '#008E00', padding: '5px', lineHeight: '1', width: 'fit-content' }}>{dayjs(date).format("MMMM D, YYYY")}</p> :
                            <p style={{ color: 'blue', padding: '5px', lineHeight: '1', width: 'fit-content' }}>{dayjs(date).format("MMMM D, YYYY")}</p>
            }
        </div>
    )
}
